import { ALERTS_URL } from 'features/alerts/paths'
import { ASK_YOGI_URL } from 'features/ask-yogi/paths'
import { CATALOG_URL } from 'features/catalog/paths'
import { CHARTS_2_URL } from 'features/charts-v2/paths'
import { CHARTS_URL } from 'features/charts/paths'
import { DASHBOARD_URL } from 'features/dashboard/paths'
import { FEEDBACK_URL } from 'features/feedback/paths'
import { KEYWORDS_URL } from 'features/keywords/paths'
import { PDP_URL } from 'features/pdp/paths'
import { RECOMMENDATIONS_URL } from 'features/recommendations/paths'
import { SAVED_VIEWS_URL } from 'features/saved-views/paths'
import { SETTINGS_URL } from 'features/settings/paths'
import { COMPARATIVE_PART, SCREEN_STATE_PAGES } from 'routes/builders'
import { DASHBOARDS_2_1_URL } from 'src/features/dashboards-v2-1/paths'
import { DASHBOARDS_2_URL } from 'src/features/dashboards-v2/paths'
import type { Surface } from 'types/project'

export const SOURCES_URL = 'sources'
export const AUTOMATED_URL = 'automated'
export const PRODUCTS_URL = 'products'
export const FINDINGS_URL = 'findings'

export const getCurrentRouteName = (pathname: string): string | undefined => {
  if (!pathname) return undefined
  const parts = pathname.split('/').filter((l) => l.length)

  if (parts.length >= 3 && parts[0] === 'project') {
    return parts[2]
  }

  const isComparative = pathname.indexOf(COMPARATIVE_PART) !== -1
  const lastPart = isComparative
    ? parts[parts.length - 2]
    : parts[parts.length - 1]

  const prevPart = isComparative
    ? parts[parts.length - 3]
    : parts[parts.length - 2]
  if (prevPart && SCREEN_STATE_PAGES.has(prevPart)) {
    return prevPart
  }

  return lastPart
}

export type { Surface }

export interface RouteDetectionResult {
  isSource: boolean
  isProduct: boolean
  isKeywords: boolean
  isSettings: boolean
  isFeedback: boolean
  isCharts: boolean
  isComparative: boolean
  isRecommendations: boolean
  isSavedViews: boolean
  isHome: boolean
  isAlerts: boolean
  isDashboard: boolean
  isFocus: boolean
  isPDP: boolean
  isAutomated: boolean
  isFindings: boolean
  isAskYogi: boolean
  surface: Surface
}

export const detectCurrentRoute = (pathname: string): RouteDetectionResult => {
  const safePath = pathname ?? ''
  const isComparative = safePath.indexOf(COMPARATIVE_PART) !== -1
  const parts = safePath.split('/').filter((l) => l.length)
  const pagePart = parts.length >= 3 && parts[0] === 'project' ? parts[2] : null
  let lastPart = pagePart
    ? pagePart
    : isComparative
      ? parts[parts.length - 2]
      : parts[parts.length - 1]

  const prevPart = isComparative
    ? parts[parts.length - 3]
    : parts[parts.length - 2]
  if (prevPart && SCREEN_STATE_PAGES.has(prevPart)) {
    lastPart = prevPart
  }

  const isFeedback = lastPart?.indexOf(FEEDBACK_URL) !== -1
  const isSource = lastPart?.indexOf(SOURCES_URL) !== -1
  const isSettings = lastPart?.indexOf(SETTINGS_URL) !== -1
  const isRecommendations = lastPart?.indexOf(RECOMMENDATIONS_URL) !== -1
  const isProduct = lastPart?.indexOf(PRODUCTS_URL) !== -1
  const isKeywords = lastPart?.indexOf(KEYWORDS_URL) !== -1
  const isCharts = lastPart === CHARTS_URL || lastPart === CHARTS_2_URL
  const isSavedViews = lastPart?.indexOf(SAVED_VIEWS_URL) !== -1
  const isAlerts = lastPart?.indexOf(ALERTS_URL) !== -1
  const isHome = parts.length == 2
  const isDashboard =
    lastPart === DASHBOARD_URL ||
    lastPart === DASHBOARDS_2_URL ||
    lastPart === DASHBOARDS_2_1_URL ||
    safePath.includes('shared-dashboard')
  const isFocus = lastPart?.indexOf(CATALOG_URL) !== -1
  const isPDP = lastPart?.indexOf(PDP_URL) !== -1
  const isAutomated = lastPart?.indexOf(AUTOMATED_URL) !== -1
  const isFindings = lastPart?.indexOf(FINDINGS_URL) !== -1
  const isAskYogi = lastPart?.indexOf(ASK_YOGI_URL) !== -1

  // Order matters: specific routes before catch-alls (home, settings).
  let surface: Surface = 'unknown'
  if (isCharts) surface = 'chart'
  else if (isDashboard) surface = 'dashboard'
  else if (isFeedback) surface = 'feedback'
  else if (isKeywords) surface = 'keywords'
  else if (isFocus) surface = 'catalog'
  else if (isSource) surface = 'sources'
  else if (isProduct) surface = 'products'
  else if (isPDP) surface = 'pdp'
  else if (isAskYogi) surface = 'ask_yogi'
  else if (isAlerts) surface = 'alerts'
  else if (isSavedViews) surface = 'saved_views'
  else if (isRecommendations) surface = 'recommendations'
  else if (isAutomated) surface = 'automated'
  else if (isFindings) surface = 'findings'
  else if (isSettings) surface = 'settings'
  else if (isHome) surface = 'home'

  return {
    isSource,
    isProduct,
    isKeywords,
    isSettings,
    isFeedback,
    isCharts,
    isComparative,
    isRecommendations,
    isSavedViews,
    isHome,
    isAlerts,
    isDashboard,
    isFocus,
    isPDP,
    isAutomated,
    isFindings,
    isAskYogi,
    surface,
  }
}
